import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Mic, Sparkles, Clock, ChevronRight, RotateCcw, MessagesSquare } from 'lucide-react';
import { trpc, type RouterOutputs } from '@org/clients/school';
import { diffCorrection, type CorrectionSegment } from '@org/utils-client';
import {
  Button,
  PracticeCall,
  Skeleton,
  useUserType,
  type PracticeCompletionResult,
  type PracticeEndOutcome,
} from '@org/ui';
import {
  P,
  oswald,
  dmSerif,
  eyebrowStyle,
  h1Style,
  h3Style,
  subStyle,
  fineStyle,
  btnRed,
  btnGhost,
  btnSmall,
  pressable,
} from '../../components/products/theme';
import mellinaImg from '../../assets/landing-mellina.webp';
import { FeatureFlagGate } from '../../components/feature-flag-gate';

// ---------------------------------------------------------------------------
// MellinAI: free conversation practice (voice) with the school's AI teacher.
// Poster visual language shared with journey / mural / central.
// ---------------------------------------------------------------------------

type Topic = RouterOutputs['conversationPractice']['getTopics']['topics'][number];
type HistoryItem = RouterOutputs['conversationPractice']['getHistory'][number];
type StartedSession = RouterOutputs['conversationPractice']['startSession'];

type View =
  | { kind: 'home' }
  | { kind: 'calling'; session: StartedSession }
  | { kind: 'result'; topic: Topic | StartedSession['topic'] | null; result: PracticeCompletionResult | null; failed: boolean }
  | { kind: 'history'; item: HistoryItem };

// Six poster combos cycling per card (journey.tsx POSTER_STYLES).
const POSTER_STYLES: Array<{ bg: string; text: string; tape: string; border?: string }> = [
  { bg: P.maxRed, text: P.white, tape: 'rgba(255,255,255,0.22)' },
  { bg: P.baby, text: P.maxRed, tape: 'rgba(212,25,32,0.14)' },
  { bg: P.black, text: P.white, tape: 'rgba(255,255,255,0.22)' },
  { bg: P.charm, text: P.darkText, tape: 'rgba(212,25,32,0.14)' },
  { bg: P.sizzle, text: P.white, tape: 'rgba(255,255,255,0.22)' },
  { bg: P.white, text: P.maxRed, tape: 'rgba(212,25,32,0.14)', border: P.lightBorder },
];
const CARD_ROTATIONS = [-1.2, 0.8, -0.6, 1.1, -0.9, 0.5];

const MELLINAI_STYLES = `
.mai-card { transition: transform .15s ease, box-shadow .15s ease; cursor: pointer; }
.mai-card:hover { transform: translateY(-4px) rotate(0deg) !important; box-shadow: 8px 12px 24px ${P.shadowDeep}; }
.mai-card:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 3px; }
.mai-card:disabled, .mai-card[aria-disabled="true"] { cursor: not-allowed; opacity: .55; }
.mai-pill { cursor: pointer; transition: background .12s ease, color .12s ease, border-color .12s ease; }
.mai-pill:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.mai-btn:hover:not(:disabled) { filter: brightness(1.06); }
.mai-row { transition: background .12s ease; cursor: pointer; }
.mai-row:hover { background: ${P.baby}; }
.mai-row:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.mai-correction { background: ${P.white}; border: 1px solid ${P.lightBorder}; border-left: 4px solid ${P.maxRed}; padding: 12px 14px; }
.mai-correction-line { display: grid; grid-template-columns: 74px 1fr; gap: 10px; align-items: baseline; }
.mai-correction-line + .mai-correction-line { margin-top: 6px; }
.mai-correction-label { font-family: ${oswald}; font-weight: 700; font-size: 10px; letter-spacing: .2em; text-transform: uppercase; }
.mai-correction-text { font-family: ${dmSerif}; font-size: 19px; line-height: 1.35; color: ${P.darkText}; }
.mai-mark-wrong { background: ${P.baby}; color: ${P.maxRed}; text-decoration: underline wavy ${P.maxRed}; text-decoration-thickness: 2px; text-underline-offset: 3px; padding: 0 3px; border-radius: 3px; }
.mai-mark-right { background: ${P.greenSoft}; color: ${P.green}; font-weight: 700; padding: 0 3px; border-radius: 3px; box-shadow: inset 0 -2px 0 ${P.green}; }
.mai-correction-why { margin: 10px 0 0; font-size: 13px; line-height: 1.5; color: ${P.midText}; }
.mai-correction-habit { display: inline-block; margin-right: 8px; padding: 1px 8px; border-radius: 999px; background: ${P.cream}; border: 1px solid ${P.lightBorder}; font-family: ${oswald}; font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: ${P.maxRed}; }
@media (max-width: 560px) { .mai-correction-line { grid-template-columns: 1fr; gap: 2px; } }
.mai-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 22px 18px; }
@media (max-width: 640px) {
  .mai-grid { grid-template-columns: 1fr 1fr; gap: 18px 12px; }
  .mai-h1 { font-size: 32px !important; }
}
@media (max-width: 420px) {
  .mai-grid { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .mai-card, .mai-pill, .mai-row { transition: none; }
  .mai-card:hover { transform: none !important; }
}
`;

export const Route = createFileRoute('/_app/mellinai')({
  component: GatedMellinAiPage,
});

function GatedMellinAiPage() {
  return (
    <FeatureFlagGate flag="mellinai">
      <MellinAiPage />
    </FeatureFlagGate>
  );
}

function Tape({ color, left = '50%', rotation = -4, width = 46 }: { color: string; left?: string; rotation?: number; width?: number }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: -8,
        left,
        transform: `translateX(-50%) rotate(${rotation}deg)`,
        width,
        height: 14,
        background: color,
        borderRadius: 1,
      }}
    />
  );
}

function formatMinutes(seconds: number): number {
  return Math.max(0, Math.floor(seconds / 60));
}

function formatDate(value: Date | string | null | undefined, locale: string): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function MellinAiPage() {
  const { t, i18n } = useTranslation('p2e');
  const isPt = i18n.language.startsWith('pt');
  const { isB2b, isLoading: isUserTypeLoading } = useUserType();
  const utils = trpc.useUtils();

  const topicsQuery = trpc.conversationPractice.getTopics.useQuery(undefined, { enabled: !isB2b });
  const quotaQuery = trpc.conversationPractice.getQuota.useQuery(undefined, { enabled: !isB2b, staleTime: 15_000 });
  const historyQuery = trpc.conversationPractice.getHistory.useQuery({ limit: 20 }, { enabled: !isB2b });
  const startMutation = trpc.conversationPractice.startSession.useMutation();

  const [view, setView] = useState<View>({ kind: 'home' });
  const [durationSec, setDurationSec] = useState<300 | 600>(600);
  const [error, setError] = useState<string | null>(null);

  const topics = topicsQuery.data?.topics ?? [];
  const regularTopics = useMemo(() => topics.filter((topic) => !topic.isChallenge), [topics]);
  const challengeTopics = useMemo(() => topics.filter((topic) => topic.isChallenge), [topics]);
  const quota = quotaQuery.data;
  const remainingMin = quota ? formatMinutes(quota.remainingSec) : null;
  const canStart = quota?.canStart ?? false;

  const titleOf = useCallback(
    (topic: { titleEn: string; titlePt: string } | null | undefined) =>
      topic ? (isPt ? topic.titlePt : topic.titleEn) : t('mellinai.history.freeStyle'),
    [isPt, t],
  );
  const descriptionOf = (topic: Topic) => (isPt ? topic.descriptionPt : topic.descriptionEn);

  const refresh = useCallback(() => {
    void utils.conversationPractice.getQuota.invalidate();
    void utils.conversationPractice.getHistory.invalidate();
  }, [utils]);

  const start = useCallback(
    async (topicId?: string, freeStyle = false) => {
      setError(null);
      try {
        const session = await startMutation.mutateAsync({ topicId, freeStyle, durationSec });
        setView({ kind: 'calling', session });
        void utils.conversationPractice.getQuota.invalidate();
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        setError(message === 'DAILY_LIMIT_REACHED' ? t('mellinai.errors.dailyLimit') : t('mellinai.errors.start'));
        refresh();
      }
    },
    [durationSec, refresh, startMutation, t, utils],
  );

  const handleEnded = useCallback(
    (session: StartedSession, outcome: PracticeEndOutcome) => {
      setView({ kind: 'result', topic: session.topic, result: outcome.result ?? null, failed: !outcome.result });
      refresh();
    },
    [refresh],
  );

  if (!isUserTypeLoading && isB2b) {
    return (
      <div style={{ background: P.cream, minHeight: '100%', padding: '32px 20px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', background: P.white, border: `1px solid ${P.lightBorder}`, padding: 28 }}>
          <p style={eyebrowStyle}>{t('mellinai.title')}</p>
          <p style={{ ...subStyle, marginTop: 8 }}>{t('mellinai.b2b.text')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: P.cream, minHeight: '100%', padding: view.kind === 'calling' ? '0 0 8px' : '28px 20px 56px' }}>
      <style>{MELLINAI_STYLES}</style>
      <div style={{ maxWidth: 1040, margin: '0 auto' }}>
        {view.kind === 'home' && (
          <>
            <Header
              cefrLevel={topicsQuery.data?.cefrLevel ?? null}
              remainingMin={remainingMin}
              dailyLimitMin={quota ? formatMinutes(quota.dailyLimitSec) : 15}
              loading={quotaQuery.isLoading}
            />

            {error && (
              <div role="alert" style={{ background: P.blush, border: `1px solid ${P.baby}`, color: P.maxRed, padding: '10px 14px', margin: '14px 0', fontSize: 14 }}>
                {error}
              </div>
            )}

            {/* Duration + surprise */}
            <section style={{ marginTop: 22, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
              <div>
                <p style={{ ...h3Style, margin: '0 0 8px' }}>{t('mellinai.duration.label')}</p>
                <div style={{ display: 'flex', gap: 8 }} role="radiogroup" aria-label={t('mellinai.duration.label')}>
                  {([300, 600] as const).map((option) => {
                    const selected = durationSec === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className="mai-pill"
                        onClick={() => setDurationSec(option)}
                        style={{
                          fontFamily: oswald,
                          fontWeight: 600,
                          fontSize: 13,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          padding: '8px 16px',
                          borderRadius: 999,
                          border: `2px solid ${selected ? P.black : P.lightBorder}`,
                          background: selected ? P.black : P.white,
                          color: selected ? P.white : P.midText,
                        }}
                      >
                        {t('mellinai.duration.option', { minutes: option / 60 })}
                      </button>
                    );
                  })}
                </div>
                {quota && canStart && quota.remainingSec < durationSec && (
                  <p style={{ ...fineStyle, margin: '8px 0 0' }}>
                    {t('mellinai.duration.shortened', { minutes: remainingMin })}
                  </p>
                )}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'flex-end' }}>
                <div style={{ textAlign: 'right' }}>
                  <Button
                    type="button"
                    className="mai-btn"
                    style={{ ...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 10 }}
                    disabled={!canStart || startMutation.isPending}
                    onClick={() => void start(undefined, true)}
                  >
                    <MessagesSquare className="h-4 w-4" />
                    {t('mellinai.free.button')}
                  </Button>
                  <p style={{ ...fineStyle, margin: '6px 0 0', maxWidth: 220 }}>{t('mellinai.free.hint')}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Button
                    type="button"
                    className="mai-btn"
                    style={{ ...btnRed, display: 'inline-flex', alignItems: 'center', gap: 10 }}
                    disabled={!canStart || startMutation.isPending || topics.length === 0}
                    onClick={() => void start()}
                  >
                    <Sparkles className="h-4 w-4" />
                    {startMutation.isPending ? t('mellinai.call.connecting') : t('mellinai.surprise.button')}
                  </Button>
                  <p style={{ ...fineStyle, margin: '6px 0 0', maxWidth: 220 }}>{t('mellinai.surprise.hint')}</p>
                </div>
              </div>
            </section>

            {/* Topics */}
            <section style={{ marginTop: 30 }}>
              <h2 style={{ fontFamily: dmSerif, fontStyle: 'italic', fontSize: 28, margin: '0 0 16px', color: P.darkText }}>
                {t('mellinai.topics.title')}
              </h2>
              {topicsQuery.isLoading ? (
                <div className="mai-grid">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-44 w-full" />
                  ))}
                </div>
              ) : regularTopics.length === 0 && challengeTopics.length === 0 ? (
                <p style={subStyle}>{t('mellinai.topics.empty')}</p>
              ) : (
                <>
                  <TopicGrid
                    topics={regularTopics}
                    titleOf={titleOf}
                    descriptionOf={descriptionOf}
                    disabled={!canStart || startMutation.isPending}
                    onPick={(topic) => void start(topic.topicId)}
                    practiceLabel={(structures) => t('mellinai.topics.practice', { structures })}
                  />
                  {challengeTopics.length > 0 && (
                    <>
                      <p style={{ ...h3Style, marginTop: 34 }}>{t('mellinai.topics.challengeTitle')}</p>
                      <TopicGrid
                        topics={challengeTopics}
                        titleOf={titleOf}
                        descriptionOf={descriptionOf}
                        disabled={!canStart || startMutation.isPending}
                        onPick={(topic) => void start(topic.topicId)}
                        practiceLabel={(structures) => t('mellinai.topics.practice', { structures })}
                        challengeBadge={t('mellinai.topics.challengeBadge')}
                        offset={3}
                      />
                    </>
                  )}
                </>
              )}
            </section>

            {/* History */}
            <section style={{ marginTop: 40 }}>
              <h2 style={{ fontFamily: dmSerif, fontStyle: 'italic', fontSize: 28, margin: '0 0 12px', color: P.darkText }}>
                {t('mellinai.history.title')}
              </h2>
              {historyQuery.isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : !historyQuery.data?.length ? (
                <p style={subStyle}>{t('mellinai.history.empty')}</p>
              ) : (
                <div style={{ background: P.white, border: `1px solid ${P.lightBorder}` }}>
                  {historyQuery.data.map((item) => (
                    <div
                      key={item.sessionId}
                      className="mai-row"
                      {...pressable(() => setView({ kind: 'history', item }))}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '12px 16px',
                        borderBottom: `1px solid ${P.lightBorder}`,
                      }}
                    >
                      <Clock className="h-4 w-4 shrink-0" style={{ color: P.lightText }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: oswald, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: P.darkText, fontSize: 14 }}>
                          {titleOf(item.topic)}
                        </div>
                        <div style={{ ...fineStyle }}>
                          {formatDate(item.startedAt, i18n.language)}
                          {item.plannedDurationSec ? ` · ${t('mellinai.call.minutes', { minutes: formatMinutes(item.plannedDurationSec) })}` : ''}
                        </div>
                      </div>
                      <ScorePill score={item.score} max={item.maxScore ?? 100} lowContent={item.evaluation?.lowContent} />
                      <ChevronRight className="h-4 w-4 shrink-0" style={{ color: P.lightText }} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {view.kind === 'calling' && (
          <CallView
            session={view.session}
            titleOf={titleOf}
            onEnded={(outcome) => handleEnded(view.session, outcome)}
            onStartError={(err) => {
              setError(err.message || t('mellinai.errors.start'));
              setView({ kind: 'home' });
              refresh();
            }}
          />
        )}

        {view.kind === 'result' && (
          <ResultView
            title={titleOf(view.topic)}
            result={view.result}
            failed={view.failed}
            canStart={canStart}
            onAgain={() => void start(view.topic?.topicId, !view.topic)}
            onBack={() => setView({ kind: 'home' })}
          />
        )}

        {view.kind === 'history' && (
          <ResultView
            title={titleOf(view.item.topic)}
            subtitle={formatDate(view.item.startedAt, i18n.language)}
            result={
              view.item.evaluation
                ? {
                    score: view.item.score ?? 0,
                    maxScore: view.item.maxScore ?? 100,
                    feedback: view.item.evaluation.overallFeedback,
                    skillAssessments: view.item.evaluation.skillAssessments,
                    corrections: view.item.evaluation.corrections,
                    lowContent: view.item.evaluation.lowContent,
                    recovered: view.item.evaluation.recovered,
                    evaluator: view.item.evaluation.evaluator,
                  }
                : null
            }
            failed={!view.item.evaluation}
            canStart={canStart}
            onBack={() => setView({ kind: 'home' })}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Header({
  cefrLevel,
  remainingMin,
  dailyLimitMin,
  loading,
}: {
  cefrLevel: string | null;
  remainingMin: number | null;
  dailyLimitMin: number;
  loading: boolean;
}) {
  const { t } = useTranslation('p2e');
  const exhausted = remainingMin !== null && remainingMin < 2;
  return (
    <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 18 }}>
      <div style={{ flex: '1 1 420px' }}>
        <p style={eyebrowStyle}>{t('mellinai.eyebrow')}</p>
        <h1 className="mai-h1" style={{ ...h1Style, fontSize: 52, display: 'flex', alignItems: 'center', gap: 14 }}>
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              width: 48,
              height: 48,
              alignItems: 'center',
              justifyContent: 'center',
              background: P.maxRed,
              color: P.white,
              borderRadius: 4,
              transform: 'rotate(-6deg)',
            }}
          >
            <Mic className="h-6 w-6" />
          </span>
          {t('mellinai.title')}
        </h1>
        <p style={{ fontFamily: dmSerif, fontStyle: 'italic', fontSize: 22, color: P.maxRed, margin: '0 0 10px', lineHeight: 1.2 }}>
          {t('mellinai.tagline')}
        </p>
        <p style={subStyle}>{t('mellinai.intro')}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <Chip label={t('mellinai.level.label')} value={cefrLevel ?? t('mellinai.level.unknown')} />
        {loading ? (
          <Skeleton className="h-9 w-48" />
        ) : (
          <Chip
            label={t('mellinai.quota.label')}
            value={
              exhausted
                ? t('mellinai.quota.exhausted')
                : t('mellinai.quota.remaining', { minutes: remainingMin ?? dailyLimitMin })
            }
            tone={exhausted ? 'alert' : 'ok'}
          />
        )}
      </div>
    </header>
  );
}

/** A sentence with only its changed words marked (wrong = wavy red, right = green). */
function Marked({ segments, kind }: { segments: CorrectionSegment[]; kind: 'wrong' | 'right' }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.changed ? (
          <mark key={index} className={kind === 'wrong' ? 'mai-mark-wrong' : 'mai-mark-right'}>
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  );
}

function Chip({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'ok' | 'alert' }) {
  const color = tone === 'alert' ? P.maxRed : tone === 'ok' ? P.green : P.midText;
  return (
    <div style={{ background: P.white, border: `1px solid ${P.lightBorder}`, padding: '6px 12px', display: 'inline-flex', gap: 10, alignItems: 'baseline' }}>
      <span style={{ ...eyebrowStyle, fontSize: 10, color: P.lightText }}>{label}</span>
      <span style={{ fontFamily: oswald, fontWeight: 600, fontSize: 15, letterSpacing: '0.04em', color }}>{value}</span>
    </div>
  );
}

function ScorePill({ score, max, lowContent }: { score: number | null | undefined; max: number; lowContent?: boolean }) {
  if (score === null || score === undefined) return null;
  const good = !lowContent && score >= max * 0.7;
  return (
    <span
      style={{
        fontFamily: oswald,
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: '0.06em',
        padding: '4px 10px',
        borderRadius: 999,
        background: good ? P.greenSoft : P.blush,
        color: good ? P.green : P.maxRed,
        whiteSpace: 'nowrap',
      }}
    >
      {score}/{max}
    </span>
  );
}

function TopicGrid({
  topics,
  titleOf,
  descriptionOf,
  onPick,
  disabled,
  practiceLabel,
  challengeBadge,
  offset = 0,
}: {
  topics: Topic[];
  titleOf: (topic: Topic) => string;
  descriptionOf: (topic: Topic) => string;
  onPick: (topic: Topic) => void;
  disabled: boolean;
  practiceLabel: (structures: string) => string;
  challengeBadge?: string;
  offset?: number;
}) {
  return (
    <div className="mai-grid">
      {topics.map((topic, index) => {
        const look = POSTER_STYLES[(index + offset) % POSTER_STYLES.length]!;
        const rotation = CARD_ROTATIONS[(index + offset) % CARD_ROTATIONS.length]!;
        const levels = topic.minLevel === topic.maxLevel ? topic.minLevel : `${topic.minLevel} · ${topic.maxLevel}`;
        return (
          <div
            key={topic.topicId}
            className="mai-card"
            aria-disabled={disabled}
            {...pressable(() => {
              if (!disabled) onPick(topic);
            })}
            style={{
              position: 'relative',
              background: look.bg,
              color: look.text,
              border: look.border ? `1px solid ${look.border}` : 'none',
              padding: '22px 18px 18px',
              minHeight: 176,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              transform: `rotate(${rotation}deg)`,
              boxShadow: `4px 6px 16px ${P.shadow}`,
            }}
          >
            <Tape color={look.tape} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: oswald, fontWeight: 600, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.85 }}>
                {levels}
              </span>
              {challengeBadge && (
                <span
                  style={{
                    fontFamily: oswald,
                    fontWeight: 700,
                    fontSize: 10,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    background: P.gold,
                    color: P.white,
                    padding: '3px 8px',
                    borderRadius: 2,
                  }}
                >
                  {challengeBadge}
                </span>
              )}
            </div>
            <h3 style={{ fontFamily: oswald, fontWeight: 700, fontSize: 22, lineHeight: 1.05, letterSpacing: '0.03em', textTransform: 'uppercase', margin: 0 }}>
              {titleOf(topic)}
            </h3>
            <p style={{ fontFamily: dmSerif, fontStyle: 'italic', fontSize: 15, lineHeight: 1.3, margin: 0, opacity: 0.95 }}>
              {descriptionOf(topic)}
            </p>
            {topic.targetStructures && (
              <p style={{ fontSize: 11.5, lineHeight: 1.4, margin: 'auto 0 0', opacity: 0.85 }}>
                {practiceLabel(topic.targetStructures)}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Two letters for the student's circle: first + last name, else the start of the nickname. */
function initialsOf(user: { given_name?: string; family_name?: string; nickname?: string; name?: string } | undefined): string | undefined {
  if (!user) return undefined;
  const first = user.given_name?.trim();
  const last = user.family_name?.trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const single = first || user.nickname?.trim() || user.name?.trim();
  if (!single) return undefined;
  const parts = single.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return single.slice(0, 2).toUpperCase();
}

function CallView({
  session,
  titleOf,
  onEnded,
  onStartError,
}: {
  session: StartedSession;
  titleOf: (topic: StartedSession['topic']) => string;
  onEnded: (outcome: PracticeEndOutcome) => void;
  onStartError: (error: Error) => void;
}) {
  const { t } = useTranslation('p2e');
  const { user } = useAuth0();
  const topicTitle = session.freeStyle ? t('mellinai.free.button') : titleOf(session.topic);
  // Airy "On Air" studio: no poster card, the view paints its own cream stage.
  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <PracticeCall
        session={{ sessionId: session.sessionId, ephemeralKey: session.ephemeralKey, durationSec: session.durationSec }}
        onEnded={onEnded}
        onStartError={onStartError}
        hostImageSrc={mellinaImg}
        guestInitials={initialsOf(user)}
        guestName={user?.given_name ?? user?.nickname}
        topicTitle={topicTitle}
      />
    </div>
  );
}

function ResultView({
  title,
  subtitle,
  result,
  failed,
  canStart,
  onAgain,
  onBack,
}: {
  title: string;
  subtitle?: string;
  result: PracticeCompletionResult | null;
  failed: boolean;
  canStart: boolean;
  onAgain?: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('p2e');
  const corrections = result?.corrections ?? [];
  const cardStyle: CSSProperties = { background: P.white, border: `1px solid ${P.lightBorder}`, padding: '18px 20px', position: 'relative' };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <p style={eyebrowStyle}>{t('mellinai.result.eyebrow')}</p>
      <h1 className="mai-h1" style={{ ...h1Style, fontSize: 36 }}>{title}</h1>
      {subtitle && <p style={{ ...fineStyle, margin: '0 0 14px' }}>{subtitle}</p>}

      {failed || !result ? (
        <div style={{ ...cardStyle, marginTop: 14 }}>
          <p style={subStyle}>{t('mellinai.result.unavailable')}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16, marginTop: 14 }}>
          <div style={{ ...cardStyle, display: 'flex', gap: 18, alignItems: 'flex-start' }}>
            <Tape color="rgba(212,25,32,0.14)" left="14%" rotation={-5} />
            <div style={{ fontFamily: oswald, fontWeight: 700, fontSize: 44, lineHeight: 1, color: result.lowContent ? P.gold : P.maxRed, minWidth: 90 }}>
              {result.score}
              <span style={{ fontSize: 16, color: P.lightText }}>/{result.maxScore}</span>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ ...h3Style, margin: '0 0 6px' }}>{t('mellinai.result.feedbackTitle')}</p>
              <p style={{ ...subStyle, margin: 0, maxWidth: 'none' }}>{result.feedback}</p>
              {result.lowContent && <p style={{ ...fineStyle, marginTop: 8, color: P.gold }}>{t('mellinai.result.lowContent')}</p>}
              {result.recovered && <p style={{ ...fineStyle, marginTop: 8 }}>{t('mellinai.result.recovered')}</p>}
            </div>
          </div>

          <div style={cardStyle}>
            <p style={{ ...h3Style, margin: '0 0 10px' }}>{t('mellinai.result.correctionsTitle')}</p>
            {corrections.length === 0 ? (
              <p style={{ ...subStyle, margin: 0 }}>{t('mellinai.result.noCorrections')}</p>
            ) : (
              <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 14 }}>
                {corrections.map((correction, index) => {
                  const diff = diffCorrection(correction.studentQuote, correction.correctedForm);
                  return (
                  <li key={index} className="mai-correction">
                    <div className="mai-correction-line">
                      <span className="mai-correction-label" style={{ color: P.maxRed }}>{t('mellinai.result.youSaid')}</span>
                      <span className="mai-correction-text">
                        <Marked segments={diff.wrong} kind="wrong" />
                      </span>
                    </div>
                    <div className="mai-correction-line">
                      <span className="mai-correction-label" style={{ color: P.green }}>{t('mellinai.result.say')}</span>
                      <span className="mai-correction-text">
                        <Marked segments={diff.right} kind="right" />
                      </span>
                    </div>
                    {(correction.explanation || correction.habit) && (
                      <p className="mai-correction-why">
                        {correction.habit && correction.habit !== 'other' && (
                          <span className="mai-correction-habit">{t('mellinai.result.habit')}: {correction.habit}</span>
                        )}
                        {correction.explanation}
                      </p>
                    )}
                  </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}>
        {onAgain && (
          <Button type="button" className="mai-btn" style={{ ...btnRed, ...btnSmall, display: 'inline-flex', gap: 8 }} disabled={!canStart} onClick={onAgain}>
            <RotateCcw className="h-4 w-4" />
            {t('mellinai.result.again')}
          </Button>
        )}
        <Button type="button" className="mai-btn" variant="ghost" style={{ ...btnGhost, ...btnSmall }} onClick={onBack}>
          {onAgain ? t('mellinai.result.another') : t('mellinai.result.back')}
        </Button>
      </div>
    </div>
  );
}
